<template>
  <tr>
    <td class="font-monospace">{{ commitment.id }}</td>
    <td>
      <MandateLinkComponent :mandateId>
        {{ mandateName }}
      </MandateLinkComponent>
      <span class="text-body-tertiary fs-10 d-block">{{
        $t("forwards.deals.commitments.granularity", { width: widthLabel })
      }}</span>
      <span
        v-if="commitment.beyond_strategy_mwh > 0"
        class="badge rounded-pill bg-danger-subtle text-danger-emphasis"
        data-testid="commitment-strategy-overridden"
      >
        {{ $t("forwards.deals.commitments.strategy_overridden") }}
      </span>
    </td>
    <td class="text-nowrap">{{ pulledOn }}</td>
    <td class="commitment-row__fill">
      <EscrowFillBarComponent :escrowed="escrowedMwh" :total="committedMwh" />
    </td>
    <td>
      <span v-if="commitment.minimum_price_snapshot">{{
        $t("forwards.deals.commitments.floor", {
          price: commitment.minimum_price_snapshot,
        })
      }}</span>
      <span v-else>{{ $t("forwards.deals.commitments.no_floor") }}</span>
      <span v-if="commitment.contango_condition_snapshot !== 'none'">
        ·
        {{
          $t(
            `forwards.deals.commitments.contango.${commitment.contango_condition_snapshot}`
          )
        }}</span
      >
    </td>
    <td class="text-body-tertiary text-nowrap">{{ releasedOn }}</td>
    <td class="text-end text-nowrap">
      <button
        type="button"
        class="btn btn-sm btn-ghost"
        :title="$t('forwards.deals.commitments.show_escrow_tooltip')"
        @click="emit('show-escrow', commitment.id)"
      >
        {{ $t("forwards.deals.commitments.show_escrow") }}
      </button>
    </td>
  </tr>
</template>

<script setup lang="ts">
import MandateLinkComponent from "@/forwards2/components/MandateLinkComponent.vue";
import EscrowFillBarComponent from "@/forwards2/components/EscrowFillBarComponent.vue";
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import type { CommitmentResponse } from "@/forwards2/forwardsTypes";
import type { PeriodWidth } from "@/forwards2/periodUtils";
import { sumBy } from "lodash-es";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/** An em dash reads as "nothing here" in every locale, so it needs no translation key. */
const NOTHING = "—";

/**
 * One pull of volume from a mandate as a ledger row: which mandate and at what granularity, when it
 * happened, how much it took, and the price conditions it snapshotted.
 *
 * A ledger line, not a control surface — the slices this pull stands behind are edited on their own
 * rows in the delivery plan. What is left here is one door: the escrow dialog, which shows who
 * supplied the certificates and, to a super admin, lets them go back to their owners (user decision
 * 2026-09-17, merging the former Contributions and Return to owner buttons).
 *
 * A pull that took volume beyond its mandate's sales strategy is marked as such; only forwards staff
 * open deals, so only they see it.
 */
const props = defineProps<{
  commitment: CommitmentResponse;
  mandateId: number;
  mandateName: string;
  /** Null for a pay-as-produced mandate, which states no width. */
  entryWidth: PeriodWidth | null;
}>();

const emit = defineEmits<{
  "show-escrow": [commitmentId: number];
}>();

const { t } = useI18n();

const widthLabel = computed(() =>
  props.entryWidth === null
    ? t("forwards.deals.commitments.pay_as_produced")
    : t(`forwards.deals.commitments.entry_width.${props.entryWidth}`)
);

const pulledOn = computed(() =>
  formatDateTimeIntoDate(props.commitment.created_at)
);
const releasedOn = computed(() =>
  props.commitment.released_at
    ? formatDateTimeIntoDate(props.commitment.released_at)
    : NOTHING
);

const committedMwh = computed(() =>
  sumBy(props.commitment.slices, (slice) => slice.volume_mwh)
);

const escrowedMwh = computed(() =>
  sumBy(props.commitment.escrow_by_company, (row) => row.mwh)
);
</script>

<style lang="scss" scoped>
.commitment-row__fill {
  min-width: 12rem;
}
</style>
